import { ContactUs } from '@components/Common/ContactUs';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { RecallContentWrapper } from '@components/Layouts/RecallContentWrapper';
import {
  NoddiLoadingScreenUpdateBooking,
  NoddiLoadingScreenWithTrans
} from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingRecordDetailsCustomer } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { BookingAddress } from 'noddi-types';
import { NoddiBasicCard, NoddiButton, NoddiLink, noddiToast } from 'noddi-ui-web';
import { BookingDetailsTabs, customerRoutes } from 'noddi-util';
import { tracking } from 'noddi-web';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EditAddressPicker } from './EditAddressPicker';

export const EditAddress = () => {
  const { slug } = useParams();

  const {
    data: booking,
    isPending: isBookingPending,
    error: bookingError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: slug! }
  });

  if (isBookingPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingError) {
    return <ApiErrorPageWithTrans error={[bookingError]} />;
  }

  return <EditAddressContent booking={booking} />;
};

const EditAddressContent = ({ booking }: { booking: BookingRecordDetailsCustomer }) => {
  const { address, id } = booking;
  const navigate = useNavigate();
  const [selectedAddress, setSelectedAddress] = useState<BookingAddress>();

  const {
    mutateAsync: updateBooking,
    isPending: isUpdateBookingPending,
    error: updateBookingError
  } = noddiAsync.usePatch({
    type: URLKeys.patchBooking,
    queryConfig: {
      onSuccess: async () => {
        tracking.track('editBookingAddress');
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getBookingInfo,
          input: { slug: booking.slug }
        });
        navigate(customerRoutes.bookingInfo.getPath({ slug: booking.slug, tab: BookingDetailsTabs.ORDER_DETAILS }));
        noddiToast.success(t`Booking address updated successfully`);
      }
    }
  });

  const {
    data: potentialConflicts,
    isLoading: isPotentialConflictsLoading,
    error: errorPotentialConflicts
  } = noddiAsync.useGet({
    type: URLKeys.getPotentialConflictsOnAddressChange,
    input: {
      bookingId: id,
      addressId: selectedAddress?.id as number
    },
    queryConfig: {
      enabled: !!selectedAddress?.id
    }
  });

  const canUpdateAddress =
    selectedAddress &&
    id &&
    // Dont use ? operator here, because we want to make sure potentialConflicts is defined and the data is falsy
    potentialConflicts &&
    !potentialConflicts.conflictingSalesItems.length &&
    !potentialConflicts.isDeliveryWindowConflicting;

  if (updateBookingError || errorPotentialConflicts) {
    return <ApiErrorPageWithTrans error={[updateBookingError, errorPotentialConflicts]} />;
  }

  if (isUpdateBookingPending) {
    return <NoddiLoadingScreenUpdateBooking />;
  }

  const areConflictsLoadedAndPresent =
    !!potentialConflicts?.conflictingSalesItems.length || potentialConflicts?.isDeliveryWindowConflicting;

  const hasConflictingSalesItems = !!potentialConflicts?.conflictingSalesItems.length;

  return (
    <RecallContentWrapper title={t`Where do you need our help?`}>
      <div className='flex flex-col gap-4'>
        <EditAddressPicker
          bookingSlug={booking.slug}
          addressId={address.id}
          selectedAddress={selectedAddress}
          hideSavedAddresses={!!areConflictsLoadedAndPresent}
          // oxlint-disable-next-line typescript/no-misused-promises -- async setSelectedAddress; React discards the returned promise, so a rejection is unhandled unless the body catches
          setSelectedAddress={async (address) => {
            setSelectedAddress(address);
          }}
          loading={isPotentialConflictsLoading}
        />
        {canUpdateAddress && (
          <NoddiButton
            className='w-full'
            // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs updateBooking(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onClick={async () => {
              await updateBooking({
                bookingId: id,
                addressId: selectedAddress.id
              });
            }}
          >
            <Trans>Update address</Trans>
          </NoddiButton>
        )}
        {hasConflictingSalesItems && (
          <div className='flex flex-col gap-4'>
            <NoddiBasicCard className='flex flex-col gap-4'>
              <div className='flex flex-col gap-1'>
                <span>
                  {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
                  <Trans>Some items in your booking cannot be delivered to this address</Trans>🥲{' '}
                  <Trans>
                    Try selecting a different address. If you want to change the services in your booking, please create
                    a new booking and cancel this one.
                  </Trans>{' '}
                  <ContactUs />
                </span>
              </div>
              <span className='font-semibold'>
                <Trans>Services we do not offer to this address</Trans>:
              </span>
              <ul className='flex flex-col gap-1'>
                {potentialConflicts.conflictingSalesItems.map((item, idx) => (
                  <li key={idx}>
                    <p>{item.name}</p>
                  </li>
                ))}
              </ul>
            </NoddiBasicCard>

            <NoddiLink startIcon='Map' target='_blank' to={customerRoutes.deliveryMap.getPath()}>
              <Trans>See map of where we currently deliver</Trans>
              <span className='sr-only'> ({t`opens in a new tab`})</span>
            </NoddiLink>
          </div>
        )}
        {!hasConflictingSalesItems && !!potentialConflicts?.isDeliveryWindowConflicting && (
          <NoddiBasicCard className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <span>
                {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
                <Trans>Your delivery window is not available in this address</Trans>🥲{' '}
                <Trans>Please create a new booking and cancel this one.</Trans> <ContactUs />
              </span>
            </div>
          </NoddiBasicCard>
        )}
      </div>
    </RecallContentWrapper>
  );
};
